import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is the difference between a Dividend King and a Dividend Aristocrat?',
    a: 'A Dividend King has raised its dividend for 50+ consecutive years and can be any US-listed company. A Dividend Aristocrat has raised its dividend for 25+ consecutive years AND must be in the S&P 500. So every Aristocrat is large-cap; not every King is. There is significant overlap — most Kings are also Aristocrats, but a King that drops out of the S&P 500 (like Black Hills, BKH) loses the Aristocrat label while keeping the King label.',
  },
  {
    q: 'How many Dividend Kings are there in 2026?',
    a: 'There are roughly 55 Dividend Kings, give or take a few each year as new companies cross 50 years and others freeze or cut their dividend. McDonald\u2019s (MCD) is at 49 years as of 2026 and is one more raise away from joining. DiviDrip\'s Kings Challenge page shows the current list, updated quarterly.',
  },
  {
    q: 'Are Dividend Achievers safer than non-dividend stocks?',
    a: 'On average, yes — historically Dividend Achievers (10+ consecutive raises) have lower volatility and smaller drawdowns than the broader market. The discipline required to raise a dividend for a decade weeds out a lot of low-quality businesses. But "safer on average" is not "safe always" — Achievers can still cut. Always read the fundamentals.',
  },
  {
    q: 'Should I buy only Kings and Aristocrats?',
    a: 'Probably not exclusively. Kings and Aristocrats are mostly mature, slow-growth businesses (consumer staples, industrials, utilities). They are great for the income-and-stability bucket of a portfolio, but a 100% Kings portfolio sacrifices growth. A blend of Kings/Aristocrats + higher-growth dividend payers (Visa, Microsoft) + a few high-yield income picks is typical.',
  },
  {
    q: 'What happens if a Dividend King cuts its dividend?',
    a: 'It immediately loses King status, and unless it later resumes raises for 50 consecutive years from scratch (essentially impossible in one investor lifetime), it will never recover the title. Recent examples: AT&T was a Dividend Aristocrat for 36 years before its 2022 cut.',
  },
];

export default function LearnKingsAristocratsAchievers() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Dividend Kings vs Aristocrats vs Achievers — Complete Guide (2026) | DiviDrip"
      description="Side-by-side breakdown of Dividend Kings (50+ years), Aristocrats (25+ years, S&P 500), and Achievers (10+ years). Tier definitions, overlap, current counts, and how to screen."
      canonical="https://twylightcrow.com/learn/dividend-kings-vs-aristocrats-vs-achievers"
      guidePath="/learn/dividend-kings-vs-aristocrats-vs-achievers"
      sectionLabel="Dividend Tiers & ETF Face-offs"
      sectionAnchor="tiers"
      kicker="Learn · Dividend tiers"
      h1="Dividend Kings vs Aristocrats vs Achievers"
      datePublished="2026-05-22"
      jsonLd={jsonLd}
      related={[
        { to: '/kings-challenge', label: 'See the live Kings Challenge leaderboard →' },
        { to: '/aristocrats-challenge', label: 'See the live Aristocrats Challenge leaderboard →' },
        { to: '/achievers-challenge', label: 'See the live Achievers Challenge leaderboard →' },
        { to: '/learn/boring-is-beautiful', label: 'Why dividend growers beat glamour stocks over 30 years' },
        { to: '/learn/first-10-tickers', label: 'The first 10 tickers — a starter portfolio' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/what-is-drip', label: 'What is DRIP? Reinvestment explained' },
        { to: '/learn/jepi-vs-jepq', label: 'JEPI vs JEPQ — covered-call yield face-off' },
        { to: '/screener', label: 'Stock Screener — filter by tier, yield, sector' },
      ]}
    >
      <p>
        Three labels every dividend investor sees: <strong>Dividend Kings</strong>,
        <strong> Dividend Aristocrats</strong>, and <strong>Dividend Achievers</strong>. They
        all share one idea — companies that have raised their dividend for many consecutive
        years — but the threshold and exchange membership differ. Here is the side-by-side.
      </p>

      <h2>The three tiers at a glance</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Tier</th>
            <th className="text-left font-semibold">Streak required</th>
            <th className="text-left font-semibold">Membership rule</th>
            <th className="text-left font-semibold">Approx. count (2026)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold text-amber-600 dark:text-amber-400">👑 King</td>
            <td>50+ years</td>
            <td>Any US-listed company</td>
            <td className="font-mono">~55</td>
          </tr>
          <tr>
            <td className="font-semibold text-yellow-600 dark:text-yellow-500">🏆 Aristocrat</td>
            <td>25+ years</td>
            <td>Must be in the S&amp;P 500</td>
            <td className="font-mono">~69</td>
          </tr>
          <tr>
            <td className="font-semibold text-orange-600 dark:text-orange-400">🎖️ Achiever</td>
            <td>10+ years</td>
            <td>Any US-listed company</td>
            <td className="font-mono">~350</td>
          </tr>
        </tbody>
      </table>

      <h2>Where the overlap is</h2>
      <p>
        Most Dividend Kings are also Aristocrats (they have 50+ years AND are in the S&amp;P 500),
        and every Aristocrat is automatically an Achiever (25 &gt; 10). But there are
        edge cases:
      </p>
      <ul>
        <li><strong>Kings that aren&apos;t Aristocrats:</strong> small-cap or mid-cap kings outside the S&amp;P 500 — e.g. Black Hills Corp (BKH), Stepan Company (SCL), Northwest Natural Holding (NWN). They have the 50-year streak but not the index membership.</li>
        <li><strong>Aristocrats that aren&apos;t Kings:</strong> companies in the 25–49 year band — e.g. McDonald&apos;s (MCD, 49 years — one more raise away from King status), Sherwin-Williams (SHW, ~46 years), AFLAC (AFL, ~43 years), Realty Income (O, ~31 years), Cintas (CTAS, ~42 years).</li>
      </ul>

      <h2>Why investors care</h2>
      <p>
        A 50-year (or 25-year) raise streak is a powerful proxy for business durability.
        Companies that survived the 1970s stagflation, the 1987 crash, the dotcom bust, the
        2008 GFC, and COVID — and kept growing their dividend through each one — almost
        certainly have:
      </p>
      <ul>
        <li>A genuine economic moat (otherwise competition would have crushed margins).</li>
        <li>Conservative payout ratios (otherwise an earnings dip would have forced a cut).</li>
        <li>Shareholder-friendly management (otherwise the buyback budget would have eaten the dividend).</li>
      </ul>
      <p>
        That doesn&apos;t make them <em>good buys at every price</em> — many Kings trade at
        premium multiples — but it does make them low-surprise holdings for the income sleeve
        of a portfolio.
      </p>

      <h2>How DiviDrip uses these tiers</h2>
      <p>
        Every stock row in DiviDrip shows a tier crown when applicable. The three Challenge
        pages — <Link to="/kings-challenge">Kings</Link>, <Link to="/aristocrats-challenge">Aristocrats</Link>,
        and <Link to="/achievers-challenge">Achievers</Link> — surface live leaderboards you
        can sort and screen. You can also filter the Stock Screener by tier to find candidates
        that match your minimum-quality bar.
      </p>

      <h2>Should you only buy the highest tier?</h2>
      <p>
        Tempting, but probably no. Kings are mostly mature businesses with low growth rates
        (think 4–8% earnings growth annually). A portfolio that&apos;s 100% Kings will deliver
        steady income but will lag a 60/40 mix of Kings + higher-growth dividend payers (MSFT,
        V, ABBV) over long periods. A common framework:
      </p>
      <ul>
        <li><strong>Stability core (40-60%)</strong>: Kings + Aristocrats — JNJ, PG, KO, MMM, WMT.</li>
        <li><strong>Growth (20-30%)</strong>: Achievers with stronger growth — MSFT, V, AAPL, ABBV.</li>
        <li><strong>Income kick (10-20%)</strong>: high-yield ETFs / REITs — JEPI, SCHD, O, MAIN.</li>
      </ul>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>
    </LearnPage>
  );
}
